import {DataTableSkeleton, Modal, Button, TableContainer} from '@carbon/react';
import {observer} from 'mobx-react';
import {memo, useState} from 'react';
import Variable from '../../../../lib/metadata';
import getFormatter from '../../../../lib/formatter';
import CustomizableClientTable from '../../../../components/customizable-client-table';
import {LoadMoreSection} from './load-more-section';
import ProviderTableContent from './provider-table-content';
import {TableHeader as ExtendedTableHeader} from './types';
import TableFilter from '../table-filter';
import type NetworkOptimizationStore from '../../lib/store';

interface LoadMoreConfig {
    currentPage: number;
    totalPages: number;
    loadingMore: boolean;
    buttonText: string;
    recordCountText: string;
    onLoadMore: () => void;
}

export interface ProviderTableShellProps {
    // Data loading state
    loading: boolean;
    error: string | null;
    tableRows: any[];

    // Headers
    tableHeaders: ExtendedTableHeader[];

    // Optional pagination
    loadMoreConfig?: LoadMoreConfig;

    // Configuration
    skeletonRowCount?: number;
    specialtyFilter: string | null;

    // Store and handlers
    networkOptimizationStore: NetworkOptimizationStore;
    setSaveRosterModalOpen: (open: boolean) => void;

    // Selection
    selection: string[];
    deselection: string[];
    selectionSet: Set<string>;
    deselectionSet: Set<string>;
    existingProviderIds: Set<string>;
    handleCheckboxChange: (rowId: string, checked: boolean, isExisting: boolean, onError: () => void) => void;

    // Sorting
    sortColumn: string;
    sortDirection: any;
    handleSort: (header: any) => () => void;

    // Scroll
    scrollToExistingProvider: string | null;

    // CSS class
    className?: string;
}

const ProviderTableShell = memo(observer(({
    loading,
    error,
    tableRows,
    tableHeaders,
    loadMoreConfig,
    skeletonRowCount = 5,
    specialtyFilter,
    networkOptimizationStore,
    setSaveRosterModalOpen,
    selection,
    deselection,
    selectionSet,
    deselectionSet,
    existingProviderIds,
    handleCheckboxChange,
    sortColumn,
    sortDirection,
    handleSort,
    scrollToExistingProvider,
    className = 'additional-providers-table h-full'
}: ProviderTableShellProps) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState<string | null>(null);
    const [filterHeader, setFilterHeader] = useState<{key: string, header: string, type?: string}>();

    const {filters} = networkOptimizationStore;
    const revisionId = networkOptimizationStore.revisionId ?? 0;

    // Error state
    if (error) {
        return <div className='error'>{error}</div>;
    }

    // Loading state
    if (loading) {
        return (
            <TableContainer className="overflow-x-auto bg-white">
                <DataTableSkeleton
                    headers={[{key: ' ', header: ' '}, ...tableHeaders.filter(h => h.defaultVisible)]}
                    rowCount={skeletonRowCount}
                    columnCount={tableHeaders.filter(h => h.defaultVisible).length + 1}
                    compact
                    showToolbar={false}
                />
            </TableContainer>
        );
    }

    return (
        <div className={className} data-testid="available-providers-table-container">
            <div data-testid="filtered-row-count" style={{display: 'none'}}>{tableRows.length}</div>
            {tableRows.length === 0 && (
                <>
                    <p data-testid="no-providers-found-text">No providers found</p>
                    {filters.length > 0 && (
                        <Button
                            onClick={() => networkOptimizationStore.filters = []}
                            data-testid="reset-filters-button"
                        >
                            Reset Filters
                        </Button>
                    )}
                </>
            )}
            {!!tableRows.length && (
                <>
                    {loadMoreConfig && (
                        <LoadMoreSection
                            currentPage={loadMoreConfig.currentPage}
                            totalPages={loadMoreConfig.totalPages}
                            loadingMore={loadMoreConfig.loadingMore}
                            buttonText={loadMoreConfig.buttonText}
                            recordCountText={loadMoreConfig.recordCountText}
                            onLoadMore={loadMoreConfig.onLoadMore}
                        />
                    )}
                    <CustomizableClientTable
                        tableHeaders={tableHeaders}
                        defaultVisibleColumns={tableHeaders.filter(h => h.defaultVisible).map(h => h.key)}
                        editColumnsButtonText="Edit Columns"
                        onVisibleColumnsChange={(columns) => {
                            networkOptimizationStore.setAdditionalProviderVisibleColumns(columns);
                        }}
                    >
                        {({visibleColumns, filteredHeaders, editColumnsButton, modalOpen: columnModalOpen}) => (
                            <ProviderTableContent
                                visibleColumns={visibleColumns}
                                filteredHeaders={filteredHeaders}
                                editColumnsButton={editColumnsButton}
                                columnModalOpen={columnModalOpen}
                                tableRows={tableRows}
                                sortColumn={sortColumn}
                                sortDirection={sortDirection}
                                handleSort={handleSort}
                                tableHeaders={tableHeaders}
                                filters={filters}
                                selection={selection}
                                deselection={deselection}
                                revisionId={revisionId}
                                networkOptimizationStore={networkOptimizationStore}
                                setSaveRosterModalOpen={setSaveRosterModalOpen}
                                existingProviderIds={existingProviderIds}
                                selectionSet={selectionSet}
                                deselectionSet={deselectionSet}
                                handleCheckboxChange={handleCheckboxChange}
                                setModalMessage={setModalMessage}
                                setModalOpen={setModalOpen}
                                scrollToExistingProvider={scrollToExistingProvider}
                                setFilterHeader={setFilterHeader}
                                specialtyFilter={specialtyFilter}
                            />
                        )}
                    </CustomizableClientTable>
                </>
            )}
            <Modal
                open={modalOpen}
                onRequestClose={() => setModalOpen(false)}
                primaryButtonText="Close"
                onRequestSubmit={() => setModalOpen(false)}
            >
                <h1>{modalMessage}</h1>
            </Modal>
            {filterHeader && (
                <TableFilter
                    data={tableRows}
                    config={{
                        variable: new Variable(
                            filterHeader.key,
                            filterHeader.header,
                            undefined,
                            filterHeader.type || 'string',
                            {'Format': filterHeader.type || 'string'},
                            getFormatter(filterHeader.type || 'string')
                        ),
                        filter: null
                    }}
                    onApply={(f: any) => {
                        setFilterHeader(undefined);
                        networkOptimizationStore.filters = [...filters, f];
                    }}
                    onCancel={() => {
                        setFilterHeader(undefined);
                    }}
                />
            )}
        </div>
    );
}));

ProviderTableShell.displayName = 'ProviderTableShell';

export default ProviderTableShell;
